import { DASH_META_ROOT } from '@revelio/auth';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

type AiSearchCsrfResponse = {
  csrfToken: string;
};

let cachedAiSearchCsrfToken: string | null = null;

const fetchAiSearchCsrfToken = async (): Promise<string> => {
  const response = await fetch(`${DASH_META_ROOT}/surf/ai-search`, {
    headers: { 'request-id': crypto.randomUUID() },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Failed to fetch AI search CSRF token (${response.status})`
    );
  }

  const { csrfToken }: AiSearchCsrfResponse = await response.json();
  return csrfToken;
};

export const getAiSearchCsrfToken = async (): Promise<string> => {
  if (cachedAiSearchCsrfToken) {
    return cachedAiSearchCsrfToken;
  }

  const csrfToken = await fetchAiSearchCsrfToken();
  cachedAiSearchCsrfToken = csrfToken;
  return csrfToken;
};

export const aiFilterSearchRequest = async ({
  prompt,
  rolesTax,
  skillsTax,
  rolesTaxAdaptive,
  skillsTaxAdaptive,
  dateGranularity,
  signal,
}: {
  prompt: string;
  rolesTax: string;
  skillsTax: number;
  /**
   * When true, `rolesTax`/`skillsTax` are sent as a fallback and the backend
   * resolves the taxonomy from the prompt's company/industry context, only
   * falling back when the prompt names no company/industry.
   */
  rolesTaxAdaptive?: boolean;
  skillsTaxAdaptive?: boolean;
  /**
   * The page's date-axis granularity. Anchors relative date windows to the
   * latest week (`week`) or the latest complete month (`month`) so "last week" /
   * "last month" resolve correctly for the page.
   */
  dateGranularity?: 'week' | 'month';
  signal?: AbortSignal;
}): Promise<{
  response: TalentDiscoveryAiFilterSearchResponseV2;
  uuid: string;
}> => {
  const csrfToken = await getAiSearchCsrfToken();
  const response = await fetch(
    `${DASH_META_ROOT}/api/ai-search/talent-discovery-v2`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta-ai-search': csrfToken,
      },
      body: JSON.stringify({
        text: prompt,
        roles_tax: rolesTax,
        skills_tax: skillsTax,
        ...(rolesTaxAdaptive ? { roles_tax_adaptive: true } : {}),
        ...(skillsTaxAdaptive ? { skills_tax_adaptive: true } : {}),
        ...(dateGranularity ? { date_granularity: dateGranularity } : {}),
      }),
      credentials: 'include',
      signal,
    }
  );
  const data: unknown = await response.json();
  return data as {
    response: TalentDiscoveryAiFilterSearchResponseV2;
    uuid: string;
  };
};
